/**
 * SessionManager - Manages device session lifecycle and caching
 *
 * Provides session caching with 30-minute expiration and concurrent request locking
 * to prevent multiple simultaneous session establishments for the same device.
 *
 * Backend Reference: iot-connect/adapters/hikvision/hikvision_adapter.go:764-832
 */

import {
  SessionInfo,
  SessionCacheEntry,
  SessionManagerConfig,
  SessionEstablishmentRequest,
} from './types';
import { IDeviceAdapter, DeviceInfo } from '../adapters/base';

/**
 * SessionManager singleton class
 * Handles session caching, expiration, and concurrent request management
 */
class SessionManager {
  private sessions: Map<string, SessionCacheEntry> = new Map();
  private locks: Map<string, Promise<SessionInfo | null>> = new Map();
  private config: Required<SessionManagerConfig>;

  constructor(config: SessionManagerConfig = {}) {
    this.config = {
      defaultTtl: config.defaultTtl || 30 * 60 * 1000, // 30 minutes
      maxCacheSize: config.maxCacheSize || 100,
      debug: config.debug || false,
      refreshThreshold: config.refreshThreshold || 0.1, // Refresh when 10% of TTL remaining
    };

    // Start cache cleanup interval (every 5 minutes)
    this.startCacheCleanup();
  }

  /**
   * Generate unique cache key for device
   * Format: vendor:deviceId:ipAddress
   */
  private generateDeviceKey(device: DeviceInfo): string {
    return `${device.vendor}:${device.id}:${device.ip_address || device.local_ip_address}`;
  }

  /**
   * Check if session is valid (not expired)
   */
  private isSessionValid(session: SessionInfo): boolean {
    return new Date() < session.expiresAt;
  }

  /**
   * Check if session needs refresh (within refresh threshold)
   */
  private needsRefresh(session: SessionInfo): boolean {
    const now = new Date().getTime();
    const expiresAt = session.expiresAt.getTime();
    const createdAt = expiresAt - this.config.defaultTtl;
    const lifetime = expiresAt - createdAt;
    const remaining = expiresAt - now;

    return remaining < lifetime * this.config.refreshThreshold;
  }

  /**
   * Get cached session or create new one
   *
   * This is the main entry point for session management.
   * It handles caching, concurrent request locking, and automatic refresh.
   *
   * @param device - Device to get/create session for
   * @param adapter - Device adapter for session establishment
   * @returns SessionInfo if successful, null if session not needed or failed
   */
  async getOrCreateSession(
    device: DeviceInfo,
    adapter: IDeviceAdapter
  ): Promise<SessionInfo | null> {
    const key = this.generateDeviceKey(device);

    if (this.config.debug) {
      console.log(` [SessionManager] Getting or creating session for device ${key}`);
    }

    // Check cache
    const cached = this.sessions.get(key);
    if (cached && this.isSessionValid(cached.session)) {
      if (this.config.debug) {
        console.log(` [SessionManager] Using cached session for device ${key}`);
      }

      // Check if session needs refresh
      if (this.needsRefresh(cached.session)) {
        if (this.config.debug) {
          console.log(` [SessionManager] Session for device ${key} needs refresh, refreshing in background`);
        }
        // Refresh in background (don't await)
        this.refreshSessionInBackground(device, adapter, key);
      }

      return cached.session;
    }

    // Check if already establishing (concurrent lock)
    const existingLock = this.locks.get(key);
    if (existingLock) {
      if (this.config.debug) {
        console.log(` [SessionManager] Waiting for existing session establishment for device ${key}`);
      }
      return existingLock;
    }

    // Establish new session
    if (this.config.debug) {
      console.log(` [SessionManager] Establishing new session for device ${key}`);
    }

    const establishPromise = this.establishNewSession(device, adapter);
    this.locks.set(key, establishPromise);

    try {
      const session = await establishPromise;
      if (session) {
        this.cacheSession(key, session);
      }
      return session;
    } finally {
      this.locks.delete(key);
    }
  }

  /**
   * Establish new session with device
   */
  private async establishNewSession(
    device: DeviceInfo,
    adapter: IDeviceAdapter
  ): Promise<SessionInfo | null> {
    try {
      // Check if device requires session auth
      const requiresSession = await adapter.requiresSessionAuth(device);
      if (!requiresSession) {
        if (this.config.debug) {
          console.log(` [SessionManager] Device ${device.id} does not require session auth`);
        }
        return null;
      }

      // Detect firmware version (optional, adapter may do this internally)
      let firmwareInfo = null;
      if (adapter.detectFirmwareVersion) {
        try {
          firmwareInfo = await adapter.detectFirmwareVersion(device);
          if (this.config.debug && firmwareInfo) {
            console.log(
              ` [SessionManager] Detected firmware for device ${device.id}: ${firmwareInfo.version} (requiresSessionAuth: ${firmwareInfo.requiresSessionAuth})`
            );
          }
        } catch (error) {
          console.warn(` [SessionManager] Failed to detect firmware version for device ${device.id}:`, error);
          // Continue anyway, adapter will handle it
        }
      }

      // Establish session (8-second timeout — ControlID devices on local network can be slow)
      const request: SessionEstablishmentRequest = {
        device,
        firmwareInfo,
        timeout: 8000,
      };

      const result = await adapter.establishSession(request);

      if (!result.success) {
        console.warn(
          ` [SessionManager] Failed to establish session for device ${device.id}: ${result.error || 'Unknown error'}`
        );
        return null;
      }

      if (this.config.debug) {
        console.log(
          ` [SessionManager] Session established for device ${device.id}, expires at ${result.session?.expiresAt.toISOString()}`
        );
      }

      return result.session || null;
    } catch (error) {
      console.error(` [SessionManager] Error establishing session for device ${device.id}:`, error);
      return null;
    }
  }

  /**
   * Cache session with expiration
   */
  private cacheSession(deviceKey: string, session: SessionInfo): void {
    // Enforce max cache size (LRU eviction)
    if (this.sessions.size >= this.config.maxCacheSize) {
      // Remove oldest entry
      const oldestKey = this.sessions.keys().next().value;
      if (oldestKey) {
        this.sessions.delete(oldestKey);
        if (this.config.debug) {
          console.log(` [SessionManager] Evicted oldest session: ${oldestKey}`);
        }
      }
    }

    const entry: SessionCacheEntry = {
      session,
      deviceKey,
      createdAt: new Date(),
    };

    this.sessions.set(deviceKey, entry);

    if (this.config.debug) {
      console.log(` [SessionManager] Cached session for device ${deviceKey}`);
    }
  }

  /**
   * Refresh session in background (non-blocking)
   */
  private async refreshSessionInBackground(
    device: DeviceInfo,
    adapter: IDeviceAdapter,
    key: string
  ): Promise<void> {
    try {
      const session = await this.establishNewSession(device, adapter);
      if (session) {
        this.cacheSession(key, session);
        if (this.config.debug) {
          console.log(` [SessionManager] Background refresh succeeded for device ${key}`);
        }
      }
    } catch (error) {
      console.warn(` [SessionManager] Background refresh failed for device ${key}:`, error);
    }
  }

  /**
   * Invalidate cached session for device
   * Useful when session expires or authentication fails
   */
  invalidateSession(device: DeviceInfo): void {
    const key = this.generateDeviceKey(device);
    const deleted = this.sessions.delete(key);

    if (this.config.debug && deleted) {
      console.log(` [SessionManager] Invalidated session for device ${key}`);
    }
  }

  /**
   * Clear all cached sessions
   */
  clearAllSessions(): void {
    const count = this.sessions.size;
    this.sessions.clear();
    this.locks.clear();

    if (this.config.debug) {
      console.log(` [SessionManager] Cleared all ${count} cached sessions`);
    }
  }

  /**
   * Get session cache statistics
   */
  getStats(): {
    cachedSessions: number;
    activeLocks: number;
    sessions: Array<{ deviceKey: string; expiresAt: Date; vendor: string }>;
  } {
    return {
      cachedSessions: this.sessions.size,
      activeLocks: this.locks.size,
      sessions: Array.from(this.sessions.values()).map((entry) => ({
        deviceKey: entry.deviceKey,
        expiresAt: entry.session.expiresAt,
        vendor: entry.session.vendor,
      })),
    };
  }

  /**
   * Start periodic cache cleanup to remove expired sessions
   */
  private startCacheCleanup(): void {
    setInterval(() => {
      const now = new Date();
      let expiredCount = 0;

      Array.from(this.sessions.entries()).forEach(([key, entry]) => {
        if (entry.session.expiresAt < now) {
          this.sessions.delete(key);
          expiredCount++;
        }
      });

      if (this.config.debug && expiredCount > 0) {
        console.log(` [SessionManager] Cleaned up ${expiredCount} expired sessions`);
      }
    }, 5 * 60 * 1000); // Run every 5 minutes
  }
}

// Export singleton instance
export const sessionManager = new SessionManager({
  debug: true, // Enable debug logging for initial rollout
});

// Export class for testing
export { SessionManager };
